var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/require-default-props */
/* eslint-disable @typescript-eslint/naming-convention */
import React, { forwardRef, useEffect, useRef } from 'react';
import clsx from 'classnames';
import { Label } from '../../../../atoms/Label';
import './TextInputStyles.scss';
/**
 * TextInput Component
 * Supports both single-line input and textarea with auto-resize
 * @version 1.0.0
 * @since 2026-01-27
 */
export const TextInput = forwardRef((_a, ref) => {
    var { id, label, required = false, error, className, multiline, placeholder } = _a, props = __rest(_a, ["id", "label", "required", "error", "className", "multiline", "placeholder"]);
    const textareaRef = useRef(null);
    const isTextarea = multiline === true;
    const autoResize = isTextarea && props.autoResize;
    const effectivePlaceholder = placeholder !== null && placeholder !== void 0 ? placeholder : ' ';
    // Auto-resize textarea to fit content
    const adjustTextareaHeight = React.useCallback(() => {
        if (autoResize && textareaRef.current) {
            // Reset height to auto to get the correct scrollHeight
            textareaRef.current.style.height = 'auto';
            // Set height to scrollHeight to fit content
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [autoResize]);
    // Adjust height on mount and when value changes
    useEffect(() => {
        adjustTextareaHeight();
    }, [adjustTextareaHeight, props.value]);
    const containerClass = clsx('qmb-ui-input', className, { 'qmb-ui-input--error': error });
    if (isTextarea) {
        const textareaProps = props;
        // Ref callback that assigns to local ref AND forwards to external ref
        const handleRef = (node) => {
            textareaRef.current = node;
            if (ref) {
                if (typeof ref === 'function') {
                    ref(node);
                }
                else {
                    ref.current = node;
                }
            }
        };
        return (React.createElement("div", { className: containerClass },
            React.createElement("textarea", Object.assign({ id: id, ref: handleRef, required: required, placeholder: effectivePlaceholder, className: clsx({ 'qmb-ui-input--auto-resize': autoResize }), onInput: adjustTextareaHeight }, textareaProps)),
            React.createElement(Label, { htmlFor: id, className: clsx({ '--required': required }) }, label),
            error && React.createElement("span", { className: "qmb-ui-input__error-message" }, error)));
    }
    const inputProps = props;
    const inputType = inputProps.type || 'text';
    return (React.createElement("div", { className: containerClass },
        React.createElement("input", Object.assign({ id: id, ref: ref, type: inputType, required: required, placeholder: effectivePlaceholder }, inputProps)),
        React.createElement(Label, { htmlFor: id, className: clsx({ '--required': required }) }, label),
        error && React.createElement("span", { className: "qmb-ui-input__error-message" }, error)));
});
TextInput.displayName = 'TextInput';
/**
 * TextArea Component (alias for TextInput with multiline)
 * @version 1.0.0
 * @since 2026-01-27
 */
export const TextArea = forwardRef((props, ref) => {
    return React.createElement(TextInput, Object.assign({}, props, { multiline: true, ref: ref }));
});
TextArea.displayName = 'TextArea';
